import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Battery, Plus, MoreVertical, Pencil, Trash2, Search, AlertCircle, FileSpreadsheet, QrCode, RefreshCw, Archive, Download } from "lucide-react";
import { ImportWizard } from "../components/ImportWizard";
import { getApiErrorMessage } from "../lib/apiErrors";
import { BATTERY_HEALTH_STATUS, countBatteriesNeedingReplacement } from "../lib/batteryStatus";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";


export const Batteries = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t, language } = useLanguage();
  const [batteries, setBatteries] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingBattery, setEditingBattery] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrBattery, setQRBattery] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [showRetireDialog, setShowRetireDialog] = useState(false);
  const [retireBattery, setRetireBattery] = useState(null);
  const [retireReason, setRetireReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formData, setFormData] = useState({
    organization_id: "",
    aircraft_id: "",
    model: "",
    serial_number: "",
    cycle_count: 0,
    cycle_limit: 300
  });

  const fetchData = useCallback(async () => {
    try {
      const [batteriesRes, orgsRes, aircraftRes] = await Promise.all([
        api.get(`/batteries`),
        api.get(`/organizations`),
        api.get(`/aircraft`)
      ]);
      setBatteries(batteriesRes.data);
      setOrganizations(orgsRes.data);
      setAircraft(aircraftRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [ t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      organization_id: scopedOrganizationId,
      cycle_count: parseInt(formData.cycle_count),
      cycle_limit: parseInt(formData.cycle_limit)
    };
    
    try {
      if (editingBattery) {
        await api.put(`/batteries/${editingBattery.id}`, payload);
        toast.success(t.messages.batteryUpdated);
      } else {
        await api.post(`/batteries`, payload);
        toast.success(t.messages.batteryAdded);
      }
      setShowDialog(false);
      setEditingBattery(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, language, t.messages.saveError));
    }
  };

  const handleDelete = async (battery) => {
    if (!window.confirm(`${t.actions.delete} ${battery.serial_number}?`)) return;
    try {
      await api.delete(`/batteries/${battery.id}`);
      toast.success(t.messages.batteryDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (battery) => {
    setEditingBattery(battery);
    setFormData({
      organization_id: battery.organization_id,
      aircraft_id: battery.aircraft_id || "",
      model: battery.model_name || battery.model,
      serial_number: battery.serial_number,
      cycle_count: battery.cycle_count,
      cycle_limit: battery.cycle_limit
    });
    setShowDialog(true);
  };

  // QR Code handling
  const handleShowQR = async (battery) => {
    setQRBattery(battery);
    try {
      const response = await api.get(`/batteries/${battery.id}/qr`, { responseType: 'blob' });
      const imageUrl = URL.createObjectURL(response.data);
      setQrImageUrl(imageUrl);
      setShowQRModal(true);
    } catch (error) {
      toast.error(t.messages?.fetchError || "Failed to generate QR code");
    }
  };

  const handleDownloadLabels = async () => {
    const selectedIds = batteries.filter(b => b.status !== 'retired').slice(0, 12).map(b => b.id);
    if (selectedIds.length === 0) {
      toast.error(t.battery?.noBatteriesToPrint || "No batteries to print");
      return;
    }
    try {
      const params = selectedIds.map(id => `ids=${id}`).join('&');
      const lang = t.locale?.startsWith('cs') ? 'cs' : 'en';
      const response = await api.get(`/batteries/qr-labels?${params}&language=${lang}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `battery-labels-${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t.battery?.downloadLabels || "Labels downloaded");
    } catch (error) {
      toast.error(t.messages?.downloadError || "Failed to download labels");
    }
  };

  // Retire handling
  const handleRetire = async () => {
    if (!retireReason.trim()) {
      toast.error(t.battery?.retireReasonRequired || "Please enter a reason");
      return;
    }
    try {
      await api.post(`/batteries/${retireBattery.id}/retire`, { reason: retireReason });
      toast.success(t.battery?.batteryRetired || "Battery retired");
      setShowRetireDialog(false);
      setRetireBattery(null);
      setRetireReason("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.message_cs || detail?.message_en || t.messages?.saveError);
    }
  };

  // Increment cycle
  const handleIncrementCycle = async (battery) => {
    try {
      await api.post(`/batteries/${battery.id}/cycle`, { charge_pct_after: 100 });
      toast.success(t.battery?.cycleIncremented || "Cycle incremented");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.message_cs || detail?.message_en || t.messages?.saveError);
    }
  };

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      aircraft_id: "",
      model: "",
      serial_number: "",
      cycle_count: 0,
      cycle_limit: 300
    });
  };

  const getStatusBadge = (battery) => {
    const status = battery.status || 'active';
    const batteryStatus = battery.battery_status;
    
    if (status === "retired") {
      return <Badge className="bg-slate-100 text-slate-600">{t.battery?.statusRetired || "Retired"}</Badge>;
    }
    if (status === "maintenance") {
      return <Badge className="bg-purple-100 text-purple-800">{t.battery?.statusMaintenance || "Maintenance"}</Badge>;
    }
    if (batteryStatus === "good") return <Badge className="bg-emerald-100 text-emerald-800">{t.fleet.good}</Badge>;
    if (batteryStatus === "warning") return <Badge className="bg-amber-100 text-amber-800">{t.fleet.warning}</Badge>;
    return <Badge className="bg-rose-100 text-rose-800">{t.fleet.replace}</Badge>;
  };

  const getCycleProgress = (count, limit) => {
    const percentage = (count / limit) * 100;
    return Math.min(percentage, 100);
  };

  const getCycleColor = (count, limit) => {
    const percentage = (count / limit) * 100;
    if (percentage >= 100) return "bg-rose-500";
    if (percentage >= 80) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getAircraftName = (acId) => {
    const ac = aircraft.find(a => a.id === acId);
    return ac ? `${ac.model}` : "-";
  };

  const filteredBatteries = batteries.filter(battery => {
    const matchesSearch = (battery.model_name || battery.model || '').toLowerCase().includes(search.toLowerCase()) ||
      (battery.serial_number || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || battery.status === statusFilter || 
      (statusFilter === 'active' && !battery.status);
    return matchesSearch && matchesStatus;
  });

  const replaceCount = countBatteriesNeedingReplacement(batteries);
  const warningCount = batteries.filter(b => b.battery_status === BATTERY_HEALTH_STATUS.WARNING).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="batteries-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.fleet.batteries}
          </h1>
          <p className="text-slate-500 mt-1">{t.pageDescriptions.batteries}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowImportWizard(true)}
            className="gap-2"
            data-testid="import-batteries-btn"
          >
            <FileSpreadsheet className="h-4 w-4" /> {t.actions?.import || 'Import'}
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingBattery(null); setShowDialog(true); }}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-battery-btn"
          >
            <Plus className="h-4 w-4" /> {t.fleet.addBattery}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {(replaceCount > 0 || warningCount > 0) && (
        <div className="flex gap-4">
          {replaceCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg" data-testid="battery-replace-alert">
              <AlertCircle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-rose-700">{replaceCount} {t.fleet.batteryReplaceAlert}</span>
            </div>
          )}
          {warningCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="battery-warning-alert">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-amber-700">{warningCount} {t.fleet.batteryApproachingAlert}</span>
            </div>
          )}
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t.battery?.searchPlaceholder || t.fleet.searchBatteries}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-batteries"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder={t.battery?.filterByStatus || "Filter by status"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.common?.all || "All"}</SelectItem>
            <SelectItem value="active">{t.battery?.statusActive || "Active"}</SelectItem>
            <SelectItem value="retired">{t.battery?.statusRetired || "Retired"}</SelectItem>
            <SelectItem value="maintenance">{t.battery?.statusMaintenance || "Maintenance"}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={handleDownloadLabels} className="gap-2">
          <Download className="h-4 w-4" />
          {t.battery?.printLabels || "Print Labels"}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.fleet.model}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.fleet.serialNumber}</TableHead>
                <TableHead>{t.fleet.assignedAircraft}</TableHead>
                <TableHead>{t.fleet.cycleUsage}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredBatteries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Battery className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.fleet.noBatteriesFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBatteries.map((battery) => (
                  <TableRow key={battery.id} data-testid={`battery-row-${battery.id}`}>
                    <TableCell className="font-medium">{battery.model_name || battery.model}</TableCell>
                    {user?.role === "platform_admin" && (
                      <TableCell className="text-sm text-slate-500">{getOrgName(battery.organization_id)}</TableCell>
                    )}
                    <TableCell className="font-mono text-xs">{battery.serial_number}</TableCell>
                    <TableCell className="text-sm">{getAircraftName(battery.aircraft_id)}</TableCell>
                    <TableCell>
                      <div className="w-32">
                        <div className="flex justify-between text-xs mb-1">
                          <span>{battery.cycle_count}</span>
                          <span className="text-slate-400">/ {battery.cycle_limit}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getCycleColor(battery.cycle_count, battery.cycle_limit)} transition-all`}
                            style={{ width: `${getCycleProgress(battery.cycle_count, battery.cycle_limit)}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(battery)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`battery-actions-${battery.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleShowQR(battery)}>
                            <QrCode className="h-4 w-4 mr-2" /> {t.battery?.qrCode || "QR Code"}
                          </DropdownMenuItem>
                          {battery.status !== 'retired' && (
                            <>
                              <DropdownMenuItem onClick={() => handleIncrementCycle(battery)}>
                                <RefreshCw className="h-4 w-4 mr-2" /> {t.battery?.incrementCycle || "Add Cycle"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(battery)}>
                                <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => { setRetireBattery(battery); setShowRetireDialog(true); }}
                                className="text-amber-600"
                              >
                                <Archive className="h-4 w-4 mr-2" /> {t.battery?.retire || "Retire"}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(battery)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBattery ? t.dialogs.editBattery : t.dialogs.addBattery}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="battery-form">
            {user?.role === "platform_admin" && (
              <div className="space-y-2">
                <Label>{t.common.organization} *</Label>
                <Select
                  value={formData.organization_id}
                  disabled
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="battery-organization">
                    <SelectValue placeholder={t.placeholders.selectOrganization} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bat_model">{t.fleet.model} *</Label>
                <Input
                  id="bat_model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                  data-testid="battery-model"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bat_serial">{t.fleet.serialNumber} *</Label>
                <Input
                  id="bat_serial"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  required
                  data-testid="battery-serial"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.fleet.assignedAircraft}</Label>
              <Select
                value={formData.aircraft_id || "__none__"}
                onValueChange={(value) => setFormData({ ...formData, aircraft_id: value === "__none__" ? "" : value })}
              >
                <SelectTrigger data-testid="battery-aircraft">
                  <SelectValue placeholder={t.fleet.selectAircraftOptional} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t.none}</SelectItem>
                  {aircraft.map((ac) => (
                    <SelectItem key={ac.id} value={ac.id}>{ac.model} ({ac.serial_number})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bat_count">{t.fleet.cycleCount}</Label>
                <Input
                  id="bat_count"
                  type="number"
                  min="0"
                  value={formData.cycle_count}
                  onChange={(e) => setFormData({ ...formData, cycle_count: e.target.value })}
                  data-testid="battery-cycle-count"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bat_limit">{t.fleet.cycleLimit}</Label>
                <Input
                  id="bat_limit"
                  type="number"
                  min="1"
                  value={formData.cycle_limit}
                  onChange={(e) => setFormData({ ...formData, cycle_limit: e.target.value })}
                  data-testid="battery-cycle-limit"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="battery-submit-btn">
                {editingBattery ? t.actions.update : t.actions.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        entityType="batteries"
        onComplete={() => fetchData()}
      />

      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={(open) => { setShowQRModal(open); if (!open) { setQrImageUrl(null); setQRBattery(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.battery?.qrCode || "QR Code"}</DialogTitle>
            <DialogDescription>
              {qrBattery?.serial_number} - {qrBattery?.model_name || qrBattery?.model}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrImageUrl && (
              <img src={qrImageUrl} alt="Battery QR Code" className="w-48 h-48 border rounded-lg" />
            )}
            <p className="text-xs text-slate-500 mt-2">{t.battery?.qrCode || "Scan to identify battery"}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRModal(false)}>{t.actions.cancel}</Button>
            {qrImageUrl && (
              <Button onClick={() => {
                const link = document.createElement('a');
                link.href = qrImageUrl;
                link.download = `battery-${qrBattery?.serial_number}-qr.png`;
                link.click();
              }}>
                <Download className="h-4 w-4 mr-2" /> {t.actions?.download || "Download"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retire Dialog */}
      <Dialog open={showRetireDialog} onOpenChange={(open) => { setShowRetireDialog(open); if (!open) { setRetireBattery(null); setRetireReason(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.battery?.retire || "Retire Battery"}</DialogTitle>
            <DialogDescription>
              {retireBattery?.serial_number} - {retireBattery?.model_name || retireBattery?.model}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">{t.battery?.retireConfirm || "Are you sure you want to retire this battery?"}</p>
            <div className="space-y-2">
              <Label>{t.battery?.retirementReason || "Reason"} *</Label>
              <Textarea
                value={retireReason}
                onChange={(e) => setRetireReason(e.target.value)}
                placeholder={t.battery?.retireReasonRequired || "Enter retirement reason..."}
                rows={3}
                data-testid="retire-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetireDialog(false)}>{t.actions.cancel}</Button>
            <Button onClick={handleRetire} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-retire-btn">
              <Archive className="h-4 w-4 mr-2" /> {t.battery?.retire || "Retire"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
